import { SimpleDocCommand } from '../../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../types/commands';
import { getApplicationForEntity } from '../../../services/applicationRegistry';
import { schemaToParameters } from '../../../utils/schemaToParameters';

export const page_read: SimpleDocCommand = {
  id: 'page_read',
  logicalName: 'page_read',
  version: 'v1',

  description: `Read content from Page entities. Returns blocks with node IDs for targeting in page_write.

**SUPPORTED ENTITIES:** Page entities (page_*)

**CRITICAL:** Use page_read BEFORE writing to understand existing structure and get node IDs.

**BLOCK TYPES (className values you'll see):**
- "page-paragraph"     → Plain paragraph
- "page-heading-1/2/3" → Headings
- "page-blockquote"    → Block quote
- "page-code"          → Code block
- "page-divider"       → Separator
- "page-bullet"        → Bullet list item
- "page-numbered"      → Numbered list item
- "page-todo"          → Checkbox task (attrs.checked: true/false)
- "page-callout"       → Callout box (attrs.variant, attrs.icon)
- "page-button"        → Action button (attrs.label, attrs.action, attrs.actionTarget)
- "page-embed"         → Entity embed (attrs.entityId, attrs.entityType)

**SELECTORS:**
{ "all": true }                           — Read everything (default)
{ "nodeId": "node_abc" }                  — Single block by ID
{ "nodeIds": ["id1", "id2"] }             — Multiple blocks by ID
{ "range": { "start": 0, "limit": 20 } } — Paginated read
{ "query": "search text" }               — Full-text search
{ "className": "page-todo" }             — All blocks of a type

**OUTPUT:** Each node has: id, className, text, attrs (block-specific metadata)

**EXAMPLES:**

Read full page:
{ "entityId": "page_river_setup_guide" }

Read first 10 blocks:
{ "entityId": "page_river_setup_guide", "selector": { "range": { "start": 0, "limit": 10 } } }

Read all todo items:
{ "entityId": "page_river_setup_guide", "selector": { "className": "page-todo" } }

Search for text:
{ "entityId": "page_river_setup_guide", "selector": { "query": "Gmail" } }

Read a specific block:
{ "entityId": "page_river_setup_guide", "selector": { "nodeId": "node_abc123" } }`,

  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        entityId: {
          type: 'string',
          description: 'llmId of the Page entity (page_*)',
        },
        selector: {
          type: 'object',
          description: 'What to read. Defaults to { all: true }. Options: all, nodeId, nodeIds, range, query, className.',
        },
      },
      required: ['entityId'],
    }),
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId, selector = { all: true } } = params;

    console.log('[page_read] Reading from:', entityId, 'selector:', selector);

    try {
      const app = await getApplicationForEntity(entityId);

      if (!app) {
        return {
          status: 'error',
          message: 'Page not found or not open',
          error: {
            code: 'app_not_found',
            category: 'validation',
            details: `No application instance found for ${entityId}. Make sure the page is open.`,
          },
          format: 'plaintext',
        };
      }

      const finalSelector =
        !selector || Object.keys(selector).length === 0 ? { all: true } : selector;

      const result = await app.handleEntityRead(finalSelector, {});

      if (result.nodes && result.nodes.length > 1000) {
        return {
          status: 'success',
          message: `Warning: Large result (${result.nodes.length} nodes). Use pagination.`,
          data: {
            entityId,
            warning: 'Result set is large. Use range selector: { range: { start: 0, limit: 100 } }',
            totalNodes: result.nodes.length,
            preview: result.nodes.slice(0, 100),
          },
          format: 'json',
        };
      }

      return {
        status: 'success',
        message: `Read ${result.nodes?.length ?? 0} blocks from ${entityId}`,
        data: {
          entityId,
          nodes: result.nodes ?? [],
          stats: {
            nodeCount: result.nodes?.length ?? 0,
            wordCount: result.wordCount ?? 0,
          },
        },
        format: 'json',
      };
    } catch (error: any) {
      console.error('[page_read] Error:', error);
      return {
        status: 'error',
        message: 'Failed to read page',
        error: {
          code: 'execution_error',
          category: 'execution',
          details: error.message || 'Unknown error',
        },
        format: 'plaintext',
      };
    }
  },
};
